import { useRef, useState } from 'react';
import { Download, FileCheck2, Upload, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';
import { storyApi } from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';
import { useToast } from '../contexts/ToastContext';
import { downloadCsv, fileTimestamp } from '../utils/csv';

interface PublishReorderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
  importPassword: string;
}

type StepStatus = { loading: boolean; error: string; success: string };

const idleStatus: StepStatus = { loading: false, error: '', success: '' };

function StepHeader({ step, title, description }: { step: number; title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {step}
      </div>
      <div>
        <h3 className="font-semibold leading-7">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function FilePicker({
  file,
  onChange,
  disabled,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed border-input bg-muted/30 p-2">
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.files?.[0] ?? null);
          // Allow picking the same file again after it changes on disk
          e.target.value = '';
        }}
      />
      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => inputRef.current?.click()}>
        <Upload className="mr-2 h-4 w-4" />
        {file ? 'Change file' : 'Choose CSV'}
      </Button>
      <div className="flex min-w-0 items-center gap-1 text-sm">
        {file ? (
          <>
            <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span className="truncate" title={file.name}>{file.name}</span>
          </>
        ) : (
          <span className="text-muted-foreground">No file selected</span>
        )}
      </div>
    </div>
  );
}

function StatusMessage({ status }: { status: StepStatus }) {
  if (status.error) {
    // The save API reports row problems as "Nothing saved. Row 2: ...; Row 5: ..."
    const prefix = 'Nothing saved.';
    const hasDetails = status.error.startsWith(prefix);
    const headline = hasDetails ? prefix : status.error;
    const details = hasDetails ? status.error.slice(prefix.length).split(';').map((d) => d.trim()).filter(Boolean) : [];
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span className="font-medium">{headline}</span>
        </div>
        {details.length > 0 && (
          <ul className="ml-6 mt-2 max-h-40 list-disc space-y-0.5 overflow-y-auto pl-4">
            {details.map((detail, i) => (
              <li key={i}>{detail}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  if (status.success) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4" />
        {status.success}
      </div>
    );
  }
  return null;
}

/**
 * Three-step flow to reorder published stories via CSV:
 * download current order, verify an edited CSV, then import it.
 */
export function PublishReorderModal({ isOpen, onClose, onImported, importPassword }: PublishReorderModalProps) {
  const { toast } = useToast();
  const [download, setDownload] = useState<StepStatus>(idleStatus);
  const [verify, setVerify] = useState<StepStatus>(idleStatus);
  const [importStatus, setImportStatus] = useState<StepStatus>(idleStatus);
  const [verifyFile, setVerifyFile] = useState<File | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);

  const busy = download.loading || verify.loading || importStatus.loading;

  const resetAndClose = () => {
    setDownload(idleStatus);
    setVerify(idleStatus);
    setImportStatus(idleStatus);
    setVerifyFile(null);
    setImportFile(null);
    onClose();
  };

  const handleClose = () => {
    if (!busy) resetAndClose();
  };

  const handleDownload = async () => {
    setDownload({ ...idleStatus, loading: true });
    try {
      const response = await storyApi.getPublishedTitles(importPassword);
      const titles: string[] = response.data.data || [];
      downloadCsv([['title'], ...titles.map((title) => [title])], `${fileTimestamp()}.csv`);
      setDownload({ ...idleStatus, success: `Downloaded ${titles.length} published ${titles.length === 1 ? 'story' : 'stories'}` });
    } catch (err) {
      setDownload({ ...idleStatus, error: getErrorMessage(err) });
    }
  };

  const handleVerify = async () => {
    if (!verifyFile) return;
    setVerify({ ...idleStatus, loading: true });
    try {
      const response = await storyApi.parseImport(verifyFile, importPassword);
      const rows: unknown[][] = response.data.data || [];
      downloadCsv(rows, `Parsed${fileTimestamp()}.csv`);
      const errorCount = rows.slice(1).filter((row) => row[2]).length;
      setVerify({
        ...idleStatus,
        success: errorCount
          ? `Parsed file downloaded. ${errorCount} row${errorCount === 1 ? ' has' : 's have'} errors — fix them before importing.`
          : 'Parsed file downloaded. All rows look good.',
      });
    } catch (err) {
      setVerify({ ...idleStatus, error: getErrorMessage(err) });
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImportStatus({ ...idleStatus, loading: true });
    try {
      await storyApi.saveImport(importFile, importPassword);
      setImportStatus(idleStatus);
      toast({ title: 'Success', description: 'Import done' });
      onImported();
      resetAndClose();
    } catch (err) {
      setImportStatus({ ...idleStatus, error: getErrorMessage(err) });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent onClose={handleClose} className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Publish & Reorder Stories</DialogTitle>
          <DialogDescription>
            Download the current order, edit it, verify it, then import. You can skip straight to Step 3 if your file is ready.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <section className="space-y-3 rounded-lg border p-4">
            <StepHeader
              step={1}
              title="Download Published Stories List"
              description="A CSV of all currently published story titles, in their current order."
            />
            <div className="pl-10 space-y-2">
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={busy}>
                {download.loading ? <Spinner size="sm" className="mr-2" /> : <Download className="mr-2 h-4 w-4" />}
                {download.loading ? 'Preparing download...' : 'Download'}
              </Button>
              <StatusMessage status={download} />
            </div>
          </section>

          <section className="space-y-3 rounded-lg border p-4">
            <StepHeader
              step={2}
              title="Verify CSV before importing"
              description="Upload your edited list of titles. You'll get back a parsed CSV with each story's DB Id and any errors."
            />
            <div className="pl-10 space-y-2">
              <FilePicker
                file={verifyFile}
                disabled={busy}
                onChange={(file) => {
                  setVerifyFile(file);
                  setVerify(idleStatus);
                }}
              />
              {verifyFile && (
                <Button size="sm" onClick={handleVerify} disabled={busy}>
                  {verify.loading ? <Spinner size="sm" className="mr-2" /> : <FileCheck2 className="mr-2 h-4 w-4" />}
                  {verify.loading ? 'Verifying titles...' : 'Verify'}
                </Button>
              )}
              <StatusMessage status={verify} />
            </div>
          </section>

          <section className="space-y-3 rounded-lg border p-4">
            <StepHeader
              step={3}
              title="Import & Set New order of stories"
              description="Upload a CSV with a “DB Id” column. Listed stories are published in that order; all other published stories move to draft."
            />
            <div className="pl-10 space-y-2">
              <FilePicker
                file={importFile}
                disabled={busy}
                onChange={(file) => {
                  setImportFile(file);
                  setImportStatus(idleStatus);
                }}
              />
              <Button size="sm" onClick={handleImport} disabled={busy || !importFile}>
                {importStatus.loading ? <Spinner size="sm" className="mr-2" /> : <Upload className="mr-2 h-4 w-4" />}
                {importStatus.loading ? 'Importing...' : 'Import'}
              </Button>
              <StatusMessage status={importStatus} />
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
